/**
 * services/web-ui/src/hooks/useCallEvents.ts
 *
 * TanStack Query hook that:
 *   1. Fetches the session detail + initial events in parallel.
 *   2. After initial load, polls for new events every 1500ms (cursor-based).
 *   3. Automatically stops polling when the session has ended.
 *
 * No SSE — uses the polling endpoint per W4-A design.
 */

import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchSession,
  fetchEvents,
  pollEvents,
  type SessionDetail,
  type SessionEvent,
} from "../api/voice-sessions";

const TERMINAL_STATUSES = new Set(["ended", "failed", "dropped"]);
const POLL_INTERVAL_MS = 1500;

export interface UseCallEventsResult {
  session: SessionDetail | undefined;
  events: SessionEvent[];
  isLoading: boolean;
  error: Error | null;
  /** True while the session is live (status in starting/active) */
  isLive: boolean;
}

export function useCallEvents(sessionId: string): UseCallEventsResult {
  // ── Initial session fetch ──────────────────────────────────────────────────
  const {
    data: session,
    isLoading: sessionLoading,
    error: sessionError,
  } = useQuery({
    queryKey: ["voice-session", sessionId],
    queryFn: () => fetchSession(sessionId),
    retry: 1,
    refetchOnWindowFocus: false,
    // Refresh session row every 10s to pick up status/stage changes
    refetchInterval: (query) => {
      const s = query.state.data;
      if (s && TERMINAL_STATUSES.has(s.status)) return false;
      return 10_000;
    },
  });

  // ── Initial events fetch ───────────────────────────────────────────────────
  const {
    data: initialEventsData,
    isLoading: eventsLoading,
    error: eventsError,
  } = useQuery({
    queryKey: ["voice-session-events", sessionId],
    queryFn: () => fetchEvents(sessionId),
    retry: 1,
    refetchOnWindowFocus: false,
    enabled: !!session, // wait for session to confirm it exists
  });

  // ── Merged event state (initial + polled) ──────────────────────────────────
  const [allEvents, setAllEvents] = useState<SessionEvent[]>([]);
  const lastTsMsRef = useRef<number>(0);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Seed state once initial events arrive
  useEffect(() => {
    if (!initialEventsData) return;
    setAllEvents(initialEventsData.events);
    lastTsMsRef.current = initialEventsData.last_ts_ms;
  }, [initialEventsData]);

  // ── Live polling ───────────────────────────────────────────────────────────
  const isLive = !!session && !TERMINAL_STATUSES.has(session.status);

  useEffect(() => {
    // Clear any running interval whenever inputs change
    if (pollingRef.current !== null) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    // Only poll if initial events are loaded and session is still live
    if (!initialEventsData || !isLive) return;

    const intervalId = setInterval(async () => {
      try {
        const result = await pollEvents(sessionId, lastTsMsRef.current);
        if (result.events.length > 0) {
          setAllEvents((prev) => {
            // Deduplicate by event id
            const existingIds = new Set(prev.map((e) => e.id));
            const newEvents = result.events.filter((e) => !existingIds.has(e.id));
            return newEvents.length > 0 ? [...prev, ...newEvents] : prev;
          });
          lastTsMsRef.current = result.last_ts_ms;
        }
      } catch {
        // Non-fatal — keep polling
      }
    }, POLL_INTERVAL_MS);

    pollingRef.current = intervalId;

    return () => {
      clearInterval(intervalId);
      pollingRef.current = null;
    };
  }, [sessionId, initialEventsData, isLive]);

  return {
    session,
    events: allEvents,
    isLoading: sessionLoading || (!!session && eventsLoading),
    error: (sessionError ?? eventsError) as Error | null,
    isLive,
  };
}
